import React from 'react';
import classnames from 'classnames/bind';
import LayerComm from 'app/components/common/LayerComm';
import { ENCARPAY_INFLOW, ENCARPAY_LIMIT } from 'app/constants/encarpay';
import { numberWithCommas } from 'app/libs/number';
import LimitShortageAmount from 'app/components/encarpay/LimitShortageAmount';
import { useUniversalCloser } from 'app/hook/common/useUniversalCloser';
import { goHome } from 'app/libs/location';
import AlignBox from 'app/components/common/AlignBox';
import Button from 'app/components/common/Button';
import styles from './EncarpayLimitResultLayer.module.scss';

const cx = classnames.bind(styles);

function EncarpayLimitResultLayer({ encarpayInfo = null, limitResult = null, estimateInfo = null, onSubmit = undefined, approvalBtnText = '확인' }) {
	const { handleUniversalClose } = useUniversalCloser({
		browser: {
			onNotClosed: goHome,
		},
	});

	const { inflow, client } = encarpayInfo || {};
	const { limitStatus, limitShortageAmount, carMaximumLimit } = limitResult || {};
	const { downPayment, carPrice } = estimateInfo || {};

	const isHomeService =
		inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY_HOME_SERVICE || inflow === ENCARPAY_INFLOW.MOBILE_INTEREST_FREE_HOME_SERVICE;
	const isHomeServiceSafety = inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY_HOME_SERVICE_SAFETY;
	const isMeetgo = inflow === ENCARPAY_INFLOW.MOBILE_ENCAR_PAY_MEET_GO;
	const isApproveEncarpay = ![ENCARPAY_LIMIT.IMPOSSIBLE, ENCARPAY_LIMIT.ERROR].includes(limitStatus);

	return (
		<LayerComm
			dimmedClick={false}
			data-enlog-dt-eventnamegroup="승인팝업"
		>
			{limitStatus === ENCARPAY_LIMIT.COMPLETELY && (
				<>
					<p className={cx('title')}>
						{client.name} 고객님, {numberWithCommas(Number(carMaximumLimit / 10000))}만원 한도까지 지불 가능하신 것이 확인되었습니다.
						<br />
					</p>
					<p className={cx('text')}>
						{(isHomeService || isMeetgo) && (
							<>
								계약금 {numberWithCommas(downPayment)}원만 입금하시면
								<br />
								차량을 받아보실 수 있습니다.
							</>
						)}
						{isHomeServiceSafety && (
							<>
								<strong>
									홈서비스 방문 픽업 계약금 {numberWithCommas(downPayment)}원을
									<br />
									선입금 하시면, 방문 당일 추가 결제 없이 차량 픽업이 가능합니다.
								</strong>
								<br />
								<br />
								차량 구매를 원하지 않으실 경우 계약금은 100% 환불됩니다.
							</>
						)}
					</p>
				</>
			)}
			{limitStatus === ENCARPAY_LIMIT.PARTLY && (
				<>
					<p className={cx('title')}>
						{client.name} 고객님, {numberWithCommas(Number(carMaximumLimit / 10000))}만원 한도까지 지불 가능하신 것이 확인되었습니다.
					</p>
					<p className={cx('text')}>
						{(isHomeService || isMeetgo) && (
							<>
								계약금 {numberWithCommas(downPayment)}원<br />+ 한도 부족 금액 *{numberWithCommas(limitShortageAmount)}원을
								<br />
								입금하시면 차량을 받아보실 수 있습니다.
							</>
						)}
						{isHomeServiceSafety && (
							<>
								<strong>
									홈서비스 방문 픽업 계약금 {numberWithCommas(downPayment)}원과
									<br />
									한도 부족 금액 {numberWithCommas(limitShortageAmount)}원을 선입금 하시면,
									<br />
									방문 당일 추가 결제 없이 차량 픽업이 가능합니다.
								</strong>
								<br />
								<br />
								차량 구매를 원하지 않으실 경우 계약금은 100% 환불됩니다.
							</>
						)}
					</p>
					<div className={cx('box')}>
						<LimitShortageAmount
							carPrice={carPrice}
							carMaximumLimit={carMaximumLimit}
							limitShortageAmount={limitShortageAmount}
						/>
					</div>
				</>
			)}
			{limitStatus === ENCARPAY_LIMIT.IMPOSSIBLE && (
				<>
					<p className={cx('title')}>
						{client.name} 고객님, 죄송합니다.
						<br />
						현재 신용상으로는
						<br />
						엔카페이 조회가 어렵습니다.
					</p>
					<p className={cx('text')}>
						{isHomeService && (
							<>
								홈서비스 신청 시, 전액현금 또는
								<br /> 현금+대출(할부)를 선택하여 신청 가능합니다.
							</>
						)}
						{isHomeServiceSafety && <>홈서비스 방문 픽업 이용을 위해서는 전액현금 선택하여 재진행해주시기 바랍니다.</>}
						{isMeetgo && (
							<>
								전액현금 또는
								<br /> 현금+대출(할부)를 선택하여 신청 가능합니다.
							</>
						)}
					</p>
				</>
			)}
			{limitStatus === ENCARPAY_LIMIT.ERROR && (
				<>
					<p className={cx('title')}>
						통신이 원활하지 않거나,
						<br />
						입력정보가 유효하지 않습니다.
					</p>
					<p className={cx('text')}>다시 진행해주시기 바랍니다.</p>
				</>
			)}
			{isApproveEncarpay ? (
				<AlignBox
					style={{ marginTop: 20 }}
					type="flex_auto"
				>
					{(isHomeServiceSafety || isMeetgo) && (
						<Button
							domType="button"
							color="white"
							size="40"
							title="닫기"
							onClick={handleUniversalClose}
							data-enlog-dt-eventname="닫기"
							data-enlog-dt-eventnamegroup="승인팝업"
						/>
					)}
					<Button
						domType="button"
						color="red"
						size="40"
						title={approvalBtnText}
						onClick={onSubmit}
						data-enlog-dt-eventname={approvalBtnText}
						data-enlog-dt-eventnamegroup="승인팝업"
						data-enlog-dt-hit="permit_purchase_apply"
					/>
				</AlignBox>
			) : (
				<Button
					style={{ marginTop: 20 }}
					domType="button"
					color="red"
					size="40"
					title="확인"
					onClick={onSubmit}
					data-enlog-dt-eventname="확인"
					data-enlog-dt-eventnamegroup="승인불가팝업"
				/>
			)}
		</LayerComm>
	);
}

export default EncarpayLimitResultLayer;
